// FriendlyDateTime — a single "date & time" field that replaces raw
// `datetime-local` inputs. One trigger button per field (showing e.g.
// "Jun 10, 2026 · 11:00 PM") opens one popover with a month calendar
// beside a scrollable time column, plus a "Custom time…" escape hatch
// for unusual times. Serializes to `YYYY-MM-DDTHH:mm` (local time) so
// the backend code that already handles that shape (see
// lib/clientCompetitions + ProgramTimeline) keeps working unchanged.
//
// The popover is portaled to document.body so a parent's overflow /
// stacking context can never clip it, and it FLIPS to open upward when
// there isn't room below the trigger (clamped fully on-screen either
// way). Web-only popover; on native the trigger renders read-only —
// the editor is web-primary.
//
// Optional fields (deliverable open/due) pass `placeholder` for a calm
// empty state and `clearable` for an in-trigger ✕ that resets to "".
//
// No new deps — react-native-svg + plain View / Pressable / Text.

import { useEffect, useMemo, useRef, useState } from "react";
import { Platform, Pressable, Text, TextInput, View, type GestureResponderEvent, type TextStyle, type ViewStyle } from "react-native";
import Svg, { Path } from "react-native-svg";
import { COLORS, FONTS } from "../../lib/brand";
import { PANE_THEMES } from "../brand/paneThemes";

// Portal helper — render the popup at document.body to bypass React
// Native Web's stacking-context weirdness. Without this, the popup
// inherits the parent SectionCard's stacking context and renders BEHIND
// sibling form sections that come later in the DOM (and can be clipped
// by any `overflow: hidden` ancestor). Portaling to body puts it at the
// top of the painting order, free of every ancestor's overflow.
function PortalToBody({ children }: { children: React.ReactNode }) {
  if (Platform.OS !== "web") return null;
  // Avoid bundling react-dom statically — react-native-web ships it as a
  // transitive dep, but we don't want a hard import in the type-check path
  // for native builds.
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { createPortal } = require("react-dom");
  if (typeof document === "undefined") return null;
  return createPortal(children, document.body);
}

const THEME = PANE_THEMES.Client;
const AMBER = THEME.accent;
const FOREST = THEME.sidebarBg;
const SAGE = COLORS.SAGE.hex;
const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const BORDER = "rgba(255,255,255,0.07)";
const BLACK_SURFACE = "rgba(0,0,0,0.4)";

// ─── Format helpers ─────────────────────────────────────────────────────

const pad2 = (n: number) => String(n).padStart(2, "0");

/** Parse "YYYY-MM-DDTHH:mm" → Date in local time. Null if invalid. */
export function parseFormDateTime(s: string | null | undefined): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(String(s).trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  return isNaN(d.getTime()) ? null : d;
}

export function formatFormDateTime(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function formatPrettyDate(d: Date): string {
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function formatPrettyTime(d: Date): string {
  const hh = d.getHours();
  const mm = d.getMinutes();
  const period = hh >= 12 ? "PM" : "AM";
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  return `${h12}:${pad2(mm)} ${period}`;
}

/** Parse "h:mm AM" / "h:mm PM" / "HH:mm". Returns null on miss. */
function parseTimeString(raw: string): { hours: number; minutes: number } | null {
  const s = raw.trim();
  if (!s) return null;
  const ampm = /^(\d{1,2}):(\d{2})\s*([AaPp])[Mm]?$/.exec(s);
  if (ampm) {
    let h = Number(ampm[1]);
    const m = Number(ampm[2]);
    const isPm = ampm[3].toUpperCase() === "P";
    if (h < 1 || h > 12 || m < 0 || m > 59) return null;
    if (h === 12) h = 0;
    if (isPm) h += 12;
    return { hours: h, minutes: m };
  }
  const military = /^(\d{1,2}):(\d{2})$/.exec(s);
  if (military) {
    const h = Number(military[1]);
    const m = Number(military[2]);
    if (h < 0 || h > 23 || m < 0 || m > 59) return null;
    return { hours: h, minutes: m };
  }
  return null;
}

// ─── 15-minute time options (6:00 AM through 11:00 PM) ─────────────────

const TIME_OPTIONS: Array<{ value: string; label: string; hours: number; minutes: number }> = [];
for (let h = 6; h <= 23; h++) {
  for (let m = 0; m < 60; m += 15) {
    const period = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    TIME_OPTIONS.push({
      value: `${pad2(h)}:${pad2(m)}`,
      label: `${h12}:${pad2(m)} ${period}`,
      hours: h,
      minutes: m,
    });
  }
}

// ─── Popup placement ────────────────────────────────────────────────────

const POPUP_WIDTH = 384;
// Estimated popup height for the flip decision: padding (20) + the taller
// of calendar (~280) / time column (288) + footer (~46). The content is
// deterministic so the estimate is stable.
const POPUP_EST_HEIGHT = 356;
const TIME_COL_HEIGHT = 288;
const TIME_ITEM_HEIGHT = 30;

/** Fixed-position style that keeps the popup fully on-screen: open below
 *  the trigger when there's room, flip above when there isn't, and as a
 *  last resort pin to the bottom edge of the viewport. */
function popupPositionStyle(anchor: DOMRect): React.CSSProperties {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const left = Math.max(8, Math.min(anchor.left, vw - POPUP_WIDTH - 8));
  const fitsBelow = anchor.bottom + 6 + POPUP_EST_HEIGHT <= vh - 8;
  const fitsAbove = anchor.top - 6 - POPUP_EST_HEIGHT >= 8;
  if (fitsBelow) return { position: "fixed", left, top: anchor.bottom + 6 };
  if (fitsAbove) return { position: "fixed", left, bottom: vh - anchor.top + 6 };
  return { position: "fixed", left, top: Math.max(8, vh - POPUP_EST_HEIGHT - 8) };
}

// ─── Component ──────────────────────────────────────────────────────────

export type FriendlyDateTimeProps = {
  /** ISO-ish "YYYY-MM-DDTHH:mm" (local). Empty = unset. */
  value: string;
  onChange: (next: string) => void;
  editable?: boolean;
  /** Tag the input for error highlighting / focus. Optional. */
  testID?: string;
  /** Tinted ring when true (e.g. validation flagged this field). */
  invalid?: boolean;
  /** Empty-state label. Optional fields use it to make the default calm
   *  and obvious, e.g. "Set a custom open date". */
  placeholder?: string;
  /** Show an in-trigger ✕ that clears back to "" when a value is set.
   *  Use for optional fields — required ones (case window) leave it off. */
  clearable?: boolean;
};

export function FriendlyDateTime(props: FriendlyDateTimeProps) {
  const { value, onChange, editable = true, invalid, clearable, placeholder } = props;
  const parsed = parseFormDateTime(value);
  const label = parsed
    ? `${formatPrettyDate(parsed)} · ${formatPrettyTime(parsed)}`
    : placeholder ?? "Pick a date & time";

  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(false);
  const [customText, setCustomText] = useState("");
  const [customErr, setCustomErr] = useState<string | null>(null);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const ref = useRef<View | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const timeListRef = useRef<HTMLDivElement | null>(null);
  const cursorWeb = Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {};

  // Capture the trigger button's screen position whenever the popup opens
  // so the portal'd popup can be absolutely positioned against it. Also
  // refresh on scroll/resize so the popup follows the trigger.
  useEffect(() => {
    if (!open || Platform.OS !== "web") return;
    function updateRect() {
      const node = ref.current as unknown as { getBoundingClientRect?: () => DOMRect } | null;
      const rect = node?.getBoundingClientRect?.();
      if (rect) setAnchorRect(rect);
    }
    updateRect();
    window.addEventListener("scroll", updateRect, true);
    window.addEventListener("resize", updateRect);
    return () => {
      window.removeEventListener("scroll", updateRect, true);
      window.removeEventListener("resize", updateRect);
    };
  }, [open]);

  // Close on outside click (web-only). The popup is portaled to body, so
  // we have to check both the trigger button AND the popup itself when
  // deciding whether a click is "outside."
  useEffect(() => {
    if (!open || Platform.OS !== "web") return;
    function onClick(e: MouseEvent) {
      const target = e.target as Node | null;
      const trigger = ref.current as unknown as HTMLElement | null;
      const popup = popupRef.current;
      if (!target) return;
      if (trigger && trigger.contains(target)) return;
      if (popup && popup.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Scroll the time column to the selected slot (or 9:00 AM) on open.
  useEffect(() => {
    if (!open) return;
    const current = parsed ? `${pad2(parsed.getHours())}:${pad2(parsed.getMinutes())}` : "09:00";
    let idx = TIME_OPTIONS.findIndex((o) => o.value === current);
    if (idx < 0) idx = TIME_OPTIONS.findIndex((o) => o.value === "09:00");
    const node = timeListRef.current;
    if (node) node.scrollTop = Math.max(0, idx * TIME_ITEM_HEIGHT - TIME_COL_HEIGHT / 2 + TIME_ITEM_HEIGHT / 2);
    // Re-run only when the popup opens — mid-edit value changes shouldn't
    // yank the list around under the cursor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function pickDate(d: Date) {
    // Carry the existing time over; default to 9 AM if no prior value.
    // Stay open so the user can adjust the time in the same popup.
    const base = parsed ?? new Date(d.getFullYear(), d.getMonth(), d.getDate(), 9, 0);
    const merged = new Date(d.getFullYear(), d.getMonth(), d.getDate(), base.getHours(), base.getMinutes());
    onChange(formatFormDateTime(merged));
  }

  function pickTime(hours: number, minutes: number) {
    const base = parsed ?? new Date();
    const merged = new Date(base.getFullYear(), base.getMonth(), base.getDate(), hours, minutes);
    onChange(formatFormDateTime(merged));
    setOpen(false);
  }

  function applyCustom() {
    const t = parseTimeString(customText);
    if (!t) {
      setCustomErr("Try formats like “6:00 PM” or “18:00”.");
      return;
    }
    setCustomErr(null);
    setCustom(false);
    pickTime(t.hours, t.minutes);
  }

  const currentValue = parsed ? `${pad2(parsed.getHours())}:${pad2(parsed.getMinutes())}` : null;

  return (
    <View ref={ref} style={{ position: "relative", marginTop: 8 } as unknown as ViewStyle}>
      <Pressable
        onPress={() => {
          if (!editable) return;
          setOpen((v) => !v);
          setCustom(false);
          setCustomErr(null);
        }}
        disabled={!editable}
        style={[
          {
            flexDirection: "row",
            alignItems: "center",
            gap: 8,
            paddingHorizontal: 14,
            paddingVertical: 12,
            borderRadius: 3,
            borderWidth: 1,
            borderColor: invalid ? THEME.destructive : open ? AMBER : BORDER,
            backgroundColor: BLACK_SURFACE,
            minHeight: 44,
            opacity: editable ? 1 : 0.7,
          } as ViewStyle,
          editable ? cursorWeb : null,
        ]}
        accessibilityRole="button"
        accessibilityLabel={parsed ? `Date and time: ${label}` : label}
      >
        <CalendarIcon color={parsed ? AMBER : WM} />
        <Text
          numberOfLines={1}
          style={{
            flex: 1,
            fontFamily: FONTS.bodyMedium,
            fontSize: 13.5,
            color: parsed ? W : WM,
          }}
        >
          {label}
        </Text>
        {clearable && parsed && editable ? (
          <Pressable
            onPress={(e: GestureResponderEvent) => {
              // Don't let the tap bubble to the trigger and toggle the popup.
              e.stopPropagation();
              onChange("");
              setOpen(false);
            }}
            hitSlop={6}
            accessibilityRole="button"
            accessibilityLabel="Clear — use the default"
            style={[{ padding: 4, borderRadius: 999 } as ViewStyle, cursorWeb]}
          >
            <ClearIcon color={WM} />
          </Pressable>
        ) : null}
        <ChevronDown color={WM} open={open} />
      </Pressable>

      {open && Platform.OS === "web" && anchorRect ? (
        <PortalToBody>
          <div
            ref={popupRef}
            style={{
              ...popupPositionStyle(anchorRect),
              zIndex: 99999,
              width: POPUP_WIDTH,
              backgroundColor: "rgb(14, 14, 14)",
              borderRadius: 6,
              border: `1px solid ${AMBER}55`,
              padding: 10,
              boxShadow: "0 10px 32px rgba(0,0,0,0.45)",
              color: "white",
              boxSizing: "border-box",
            }}
          >
            {/* Calendar beside a scrollable time column — one popup, one pick flow. */}
            <div style={{ display: "flex", flexDirection: "row", gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <CalendarMonth selected={parsed} onPick={pickDate} />
              </div>
              <div
                ref={timeListRef}
                style={{
                  width: 104,
                  height: TIME_COL_HEIGHT,
                  overflowY: "auto",
                  borderLeft: `1px solid ${BORDER}`,
                  paddingLeft: 6,
                  flexShrink: 0,
                }}
              >
                {TIME_OPTIONS.map((o) => {
                  const sel = currentValue === o.value;
                  return (
                    <Pressable
                      key={o.value}
                      onPress={() => pickTime(o.hours, o.minutes)}
                      style={[
                        {
                          height: TIME_ITEM_HEIGHT,
                          justifyContent: "center",
                          paddingHorizontal: 8,
                          borderRadius: 3,
                          backgroundColor: sel ? "rgba(181,135,58,0.16)" : "transparent",
                        } as ViewStyle,
                        cursorWeb,
                      ]}
                    >
                      <Text
                        style={{
                          fontFamily: sel ? FONTS.bodyBold : FONTS.body,
                          fontSize: 12,
                          color: sel ? AMBER : WS,
                        }}
                      >
                        {o.label}
                      </Text>
                    </Pressable>
                  );
                })}
              </div>
            </div>

            {/* Footer — custom-time escape hatch + Done. */}
            <div
              style={{
                marginTop: 10,
                paddingTop: 10,
                borderTop: `1px solid ${BORDER}`,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {custom ? (
                <>
                  <TextInput
                    value={customText}
                    onChangeText={(t) => {
                      setCustomText(t);
                      setCustomErr(null);
                    }}
                    placeholder="6:00 PM"
                    placeholderTextColor={WL}
                    autoFocus
                    onSubmitEditing={applyCustom}
                    style={{
                      flex: 1,
                      paddingHorizontal: 10,
                      paddingVertical: 7,
                      backgroundColor: BLACK_SURFACE,
                      borderWidth: 1,
                      borderColor: BORDER,
                      borderRadius: 3,
                      color: W,
                      fontFamily: FONTS.body,
                      fontSize: 12.5,
                      ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as object) : {}),
                    } as unknown as TextStyle}
                  />
                  <Pressable
                    onPress={() => {
                      setCustom(false);
                      setCustomErr(null);
                    }}
                    style={[{ paddingHorizontal: 10, paddingVertical: 7, borderRadius: 3, borderWidth: 1, borderColor: BORDER } as ViewStyle, cursorWeb]}
                  >
                    <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: WS }}>Back</Text>
                  </Pressable>
                  <Pressable
                    onPress={applyCustom}
                    style={[{ paddingHorizontal: 12, paddingVertical: 7, borderRadius: 3, backgroundColor: AMBER } as ViewStyle, cursorWeb]}
                  >
                    <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 11, color: FOREST }}>Set</Text>
                  </Pressable>
                </>
              ) : (
                <>
                  <Pressable
                    onPress={() => {
                      setCustom(true);
                      setCustomText(parsed ? formatPrettyTime(parsed) : "");
                    }}
                    style={[{ flexDirection: "row", alignItems: "center", gap: 6, paddingVertical: 4 } as ViewStyle, cursorWeb]}
                  >
                    <ClockIcon color={AMBER} />
                    <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11.5, color: AMBER }}>Custom time…</Text>
                  </Pressable>
                  <div style={{ flex: 1 }} />
                  <Pressable
                    onPress={() => setOpen(false)}
                    style={[{ paddingHorizontal: 14, paddingVertical: 7, borderRadius: 3, backgroundColor: AMBER } as ViewStyle, cursorWeb]}
                  >
                    <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 11, color: FOREST }}>Done</Text>
                  </Pressable>
                </>
              )}
            </div>
            {customErr ? (
              <Text style={{ marginTop: 6, fontFamily: FONTS.bodyMedium, fontSize: 11, color: THEME.destructive }}>
                {customErr}
              </Text>
            ) : null}
          </div>
        </PortalToBody>
      ) : null}
    </View>
  );
}

// ─── Calendar month grid ────────────────────────────────────────────────

function CalendarMonth({
  selected,
  onPick,
}: {
  selected: Date | null;
  onPick: (d: Date) => void;
}) {
  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);
  const initial = selected ?? today;
  const [viewMonth, setViewMonth] = useState(new Date(initial.getFullYear(), initial.getMonth(), 1));
  const cursorWeb = Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {};

  const monthName = viewMonth.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  // Build the 6-week grid starting on Sunday.
  const grid = useMemo(() => {
    const first = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
    const startDay = first.getDay(); // 0=Sun
    const last = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0).getDate();
    const cells: Array<{ day: number | null; date: Date | null }> = [];
    for (let i = 0; i < startDay; i++) cells.push({ day: null, date: null });
    for (let d = 1; d <= last; d++) {
      cells.push({
        day: d,
        date: new Date(viewMonth.getFullYear(), viewMonth.getMonth(), d),
      });
    }
    while (cells.length % 7 !== 0) cells.push({ day: null, date: null });
    return cells;
  }, [viewMonth]);

  return (
    <View>
      {/* Header */}
      <View style={{ flexDirection: "row", alignItems: "center", paddingHorizontal: 6, paddingVertical: 6 }}>
        <Pressable
          onPress={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))}
          style={[{ paddingHorizontal: 8, paddingVertical: 6 }, cursorWeb]}
          accessibilityLabel="Previous month"
        >
          <Text style={{ color: WS, fontSize: 16 }}>‹</Text>
        </Pressable>
        <Text
          style={{
            flex: 1,
            textAlign: "center",
            fontFamily: FONTS.bodyMedium,
            fontSize: 13,
            color: W,
          }}
        >
          {monthName}
        </Text>
        <Pressable
          onPress={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))}
          style={[{ paddingHorizontal: 8, paddingVertical: 6 }, cursorWeb]}
          accessibilityLabel="Next month"
        >
          <Text style={{ color: WS, fontSize: 16 }}>›</Text>
        </Pressable>
      </View>
      {/* Weekday strip */}
      <View style={{ flexDirection: "row" }}>
        {["S", "M", "T", "W", "T", "F", "S"].map((w, i) => (
          <View key={i} style={{ flex: 1, paddingVertical: 6, alignItems: "center" }}>
            <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 9, letterSpacing: 1.2, color: WL }}>{w}</Text>
          </View>
        ))}
      </View>
      {/* Day grid (6 rows × 7) */}
      <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
        {grid.map((cell, i) => {
          const isSel =
            !!cell.date &&
            selected &&
            cell.date.getFullYear() === selected.getFullYear() &&
            cell.date.getMonth() === selected.getMonth() &&
            cell.date.getDate() === selected.getDate();
          const isToday =
            !!cell.date &&
            cell.date.getFullYear() === today.getFullYear() &&
            cell.date.getMonth() === today.getMonth() &&
            cell.date.getDate() === today.getDate();
          const isPast = !!cell.date && cell.date.getTime() < today.getTime();
          return (
            <Pressable
              key={i}
              onPress={() => {
                if (cell.date) onPick(cell.date);
              }}
              disabled={!cell.date}
              style={[
                {
                  width: `${100 / 7}%`,
                  aspectRatio: 1,
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 4,
                  backgroundColor: isSel ? "rgba(181,135,58,0.18)" : "transparent",
                  borderWidth: isSel || isToday ? 1 : 0,
                  borderColor: isSel ? AMBER : isToday ? SAGE + "88" : "transparent",
                } as ViewStyle,
                cell.date ? cursorWeb : null,
              ]}
            >
              {cell.day ? (
                <Text
                  style={{
                    fontFamily: isSel ? FONTS.bodyBold : FONTS.body,
                    fontSize: 12.5,
                    color: isSel ? AMBER : isPast ? WL : W,
                  }}
                >
                  {cell.day}
                </Text>
              ) : null}
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}

// ─── Icons ──────────────────────────────────────────────────────────────

function CalendarIcon({ color }: { color: string }) {
  return (
    <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
      <Path d="M3 2v1.5M11 2v1.5M2 5.5h10M2.5 4h9a.5.5 0 0 1 .5.5v6.5a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4.5a.5.5 0 0 1 .5-.5z" stroke={color} strokeWidth={1.2} strokeLinecap="round" />
    </Svg>
  );
}

function ClockIcon({ color }: { color: string }) {
  return (
    <Svg width={12} height={12} viewBox="0 0 14 14" fill="none">
      <Path d="M7 1.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11zM7 4v3l2 1.25" stroke={color} strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

function ClearIcon({ color }: { color: string }) {
  return (
    <Svg width={11} height={11} viewBox="0 0 10 10" fill="none">
      <Path d="M2 2l6 6M8 2L2 8" stroke={color} strokeWidth={1.4} strokeLinecap="round" />
    </Svg>
  );
}

function ChevronDown({ color, open }: { color: string; open?: boolean }) {
  return (
    <View style={{ transform: open ? [{ rotate: "180deg" }] : undefined } as unknown as ViewStyle}>
      <Svg width={10} height={10} viewBox="0 0 10 10" fill="none">
        <Path d="M2 3.5L5 6.5L8 3.5" stroke={color} strokeWidth={1.4} strokeLinecap="round" strokeLinejoin="round" />
      </Svg>
    </View>
  );
}
